import { useMemo } from 'react';
import { FCX, Tooltip, useUuid } from '@datassential/platform-ui-lib';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import BarChart from '@components/BarChart/BarChart';
import { ConsumerRatingsPreset } from '@apiHooks/useHomeConsumerRatings';
import './ConsumerRatingsNPS.scss';

const formatValue = (value: number) => value > 0 ? `+${value}` : `${value}`;

interface ConsumerRatingsNPSProps {
    selectedPreset: ConsumerRatingsPreset;
    currentChain: {
        chainId: number;
        chainName: string;
        nps: number;
    }
}

const ConsumerRatingsNPS: FCX<ConsumerRatingsNPSProps> = ({ selectedPreset, currentChain }) => {
    const tooltipId = useUuid();

    const chartData = useMemo(
        () =>
            [currentChain, ...(selectedPreset?.topChains ?? [])].map((chain) => ({
                id: chain.chainId ?? 0,
                name: chain.chainName ?? '',
                value: chain.nps ?? 0,
            })),
        [currentChain, selectedPreset],
    );

    const averageLine = useMemo(() => {
        if (chartData.length === 0) return null;

        const values = chartData.map((d) => d.value);
        const max = Math.max(...values);
        const min = Math.min(...values);
        const range = max - min;

        if (range === 0) return null;

        const averageNps = selectedPreset.averageNps ?? 0;
        const position = ((averageNps - min) / range) * 100;

        return (
            <div
                className="ConsumerRatingsNPS__average-line"
                style={{ bottom: `${position}%`, animationDuration: `${chartData.length * 150}ms` }}
            >
                <span className="ConsumerRatingsNPS__average-label">
                    {formatValue(averageNps)}
                </span>
            </div>
        );
    }, [chartData, selectedPreset]);

    if (!chartData.length) return null;

    return (
        <div className="ConsumerRatingsNPS">
            <div className="ConsumerRatingsNPS__header">
                <div className="ConsumerRatingsNPS__title">
                    <span>Net Promoter Score</span>
                    <AiOutlineInfoCircle
                        className="ConsumerRatingsNPS__info-icon"
                        data-tooltip-id={tooltipId}
                    />
                    <Tooltip id={tooltipId} place="top" style={{ zIndex: 1, maxWidth: 270 }}>
                        Net Promoter Score (NPS) is a key measure of customer satisfaction. Scores range
                        from -100 to +100. Scores above +30 are considered very good for chain
                        restaurants.
                    </Tooltip>
                </div>
                <div className="ConsumerRatingsNPS__legend">
                    <div className="ConsumerRatingsNPS__legend-item">
                        <span className="ConsumerRatingsNPS__legend-line is-chain" />
                        Chain
                    </div>
                    <div className="ConsumerRatingsNPS__legend-item">
                        <span className="ConsumerRatingsNPS__legend-line is-segment" />
                        {selectedPreset.name}
                    </div>
                </div>
            </div>
            <div key={selectedPreset.id} className="ConsumerRatingsNPS__chart">
                <BarChart
                    className="ConsumerRatingsNPS__bar-chart"
                    data={chartData}
                    formatValue={formatValue}
                    barsLegend={averageLine}
                    activeBarId={currentChain.chainId}
                />
            </div>
        </div>
    );
};

export default ConsumerRatingsNPS;
